import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useThemeMode } from '../ThemeContext';
import './NavDropdown.css';

const isInternalPath = (href = '') => href.startsWith('/');

const MenuLink = ({ item, className, style, onClick, children }) => {
  const sharedProps = { className, style, onClick };

  if (isInternalPath(item.href)) {
    return (
      <Link to={item.href} {...sharedProps}>
        {children}
      </Link>
    );
  }

  return (
    <a href={item.href} {...sharedProps}>
      {children}
    </a>
  );
};

const DropdownItem = ({ item, isLast, mode, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const panelBg = mode === 'dark' ? '#1a1a1a' : '#ffffff';
  const panelBorder = mode === 'dark' ? '#333' : '#e0e0e0';
  const textColor = isHovered ? '#26C3C0' : mode === 'dark' ? '#fff' : '#333';
  const rowBg = isHovered
    ? mode === 'dark'
      ? 'rgba(38, 195, 192, 0.1)'
      : 'rgba(38, 195, 192, 0.05)'
    : 'transparent';

  return (
    <div
      className={`nav-dropdown-item-row${hasChildren ? ' has-flyout' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        borderBottom: !isLast ? `1px solid ${mode === 'dark' ? '#333' : '#f0f0f0'}` : 'none',
      }}
    >
      {(() => {
        const rowStyle = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 20px',
          textDecoration: 'none',
          color: textColor,
          backgroundColor: rowBg,
          fontSize: 14,
          fontWeight: hasChildren ? 600 : 400,
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
          cursor: hasChildren && !item.href ? 'default' : 'pointer',
        };
        const rowContent = (
          <>
            <span>{item.label}</span>
            {hasChildren && (
              <span
                className="nav-dropdown-flyout-arrow"
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '4px solid transparent',
                  borderBottom: '4px solid transparent',
                  borderLeft: `5px solid ${isHovered ? '#26C3C0' : mode === 'dark' ? '#aaa' : '#888'}`,
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />
            )}
          </>
        );

        // Parent with children and no href = label only (opens flyout on hover)
        if (hasChildren && !item.href) {
          return (
            <div className="nav-dropdown-item-link" style={rowStyle} role="button" tabIndex={0}>
              {rowContent}
            </div>
          );
        }

        return (
          <MenuLink
            item={item}
            className="nav-dropdown-item-link"
            onClick={onClose}
            style={rowStyle}
          >
            {rowContent}
          </MenuLink>
        );
      })()}

      {hasChildren && (
        <div
          className="nav-dropdown-flyout"
          style={{
            backgroundColor: panelBg,
            border: `1px solid ${panelBorder}`,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          }}
        >
          {item.children.map((child, index) => (
            <FlyoutChild
              key={`${child.href || 'item'}-${child.label}`}
              item={child}
              isLast={index === item.children.length - 1}
              mode={mode}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FlyoutChild = ({ item, isLast, mode, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MenuLink
        item={item}
        onClick={onClose}
        style={{
          display: 'block',
          padding: '11px 20px',
          textDecoration: 'none',
          color: isHovered ? '#26C3C0' : mode === 'dark' ? '#fff' : '#333',
          backgroundColor: isHovered
            ? mode === 'dark'
              ? 'rgba(38, 195, 192, 0.1)'
              : 'rgba(38, 195, 192, 0.05)'
            : 'transparent',
          fontSize: 13,
          fontWeight: 400,
          transition: 'all 0.2s ease',
          borderBottom: !isLast ? `1px solid ${mode === 'dark' ? '#333' : '#f0f0f0'}` : 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {item.label}
      </MenuLink>
    </div>
  );
};

const NavDropdown = ({ label, items, href = '#' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { mode } = useThemeMode();
  const dropdownRef = useRef(null);

  useEffect(() => {
    setIsOpen(false);
    setIsHovered(false);
  }, [mode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const linkColor = isHovered ? '#26C3C0' : mode === 'dark' ? '#fff' : '#333';
  const arrowColor = isHovered ? '#26C3C0' : mode === 'dark' ? '#ccc' : '#666';
  const panelBg = mode === 'dark' ? '#1a1a1a' : '#ffffff';
  const panelBorder = mode === 'dark' ? '#333' : '#e0e0e0';

  return (
    <div
      ref={dropdownRef}
      className="nav-dropdown-root"
      style={{
        position: 'relative',
        display: 'inline-block',
        margin: '0 9px',
      }}
    >
      <a
        href={href}
        className="nav-dropdown-link"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((open) => !open);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          textDecoration: 'none',
          color: linkColor,
          fontSize: 18,
          fontWeight: 200,
          padding: '8px 0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          userSelect: 'none',
          transition: 'color 0.2s ease',
        }}
      >
        <span>{label}</span>
        <span
          style={{
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `4px solid ${arrowColor}`,
            transition: 'all 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
            marginLeft: 4,
            marginTop: 1,
          }}
        />
      </a>

      {/* Always rendered (hidden when closed) so menu links exist in the
          pre-rendered HTML that search engines read. */}
      <div
          className="nav-dropdown-panel"
          aria-hidden={!isOpen}
          style={{
            display: isOpen ? 'block' : 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: 280,
            zIndex: 9999,
            backgroundColor: panelBg,
            border: `1px solid ${panelBorder}`,
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '8px 0',
            marginTop: 8,
            overflow: 'visible',
          }}
        >
          {items.map((item, index) => (
            <DropdownItem
              key={`${item.href || 'group'}-${item.label}`}
              item={item}
              isLast={index === items.length - 1}
              mode={mode}
              onClose={() => setIsOpen(false)}
            />
          ))}
        </div>
    </div>
  );
};

export default NavDropdown;
